/**
 * Escapes a string for safe insertion into HTML context.
 * Converts the five characters that can break out of an HTML attribute
 * or text node into their named entity equivalents.
 *
 * Use this for every piece of user-supplied content that goes into innerHTML.
 * Safe values (enum literals, numbers, formatted dates) do not need it.
 */
export function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g,  '&amp;')
    .replace(/</g,  '&lt;')
    .replace(/>/g,  '&gt;')
    .replace(/"/g,  '&quot;')
    .replace(/'/g,  '&#x27;');
}

/**
 * Format a dollar amount with commas and optional decimal places.
 */
export function fmt$(n, decimals = 0) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/**
 * Format a dollar amount with an explicit sign for positive values.
 *
 * The sign is derived from the number and cannot be passed in. Call sites that
 * hardcode a prefix are only correct while the value always has the sign its
 * label implies, and the Win/loss breakdown proved that assumption false in
 * both directions: `bestTrade` is Math.max over closed trades, so an account
 * whose trades all lost renders the largest loss and printed "+-$50"; and
 * `worstTrade` is Math.min, so an account whose trades all won renders the
 * smallest win as a bare "$50" in loss red. Every individual figure was
 * correct; only the sign asserted over it was wrong.
 *
 * Zero gets no sign — it is neither a gain nor a loss, the same distinction
 * the trade counts draw for break-even closes (UX_AUDIT V-10).
 */
export function signed$(n, decimals = 0) {
  if (n == null) return '—';
  return (n > 0 ? '+' : '') + fmt$(n, decimals);
}

/**
 * Format an ISO date string (YYYY-MM-DD) as "Jan 5" style.
 */
export function fmtDate(s) {
  if (!s) return '';
  const [, m, d] = s.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m-1]} ${+d}`;
}

export function isOverdue(dueDate) {
  return dueDate && dueDate < new Date().toISOString().split('T')[0];
}

/**
 * Renders a task's due-date badge, differentiating hard vs soft deadlines —
 * previously `deadlineType` was collected by the Add/Edit Task Form and
 * stored, but nothing downstream ever read it, so a "hard deadline" and a
 * "soft target" looked identical everywhere. A hard deadline gets a stronger
 * visual treatment once overdue (bold, background tint), and a subtle
 * always-on tint even before it's overdue so the stakes are visible ahead of
 * time, not just after the fact.
 */
export function deadlineBadge(dueDate, deadlineType) {
  if (!dueDate) return '';
  const overdue = isOverdue(dueDate);
  const isHard = deadlineType === 'hard';
  const cls = `task-due${overdue ? ' overdue' : ''}${isHard ? ' hard' : ''}`;
  const label = overdue && isHard ? `⚠ OVERDUE ${fmtDate(dueDate)}` : `Due ${fmtDate(dueDate)}`;
  return `<span class="${cls}">${label}</span>`;
}

export function priorityBadge(p) {
  // p is always an enum value from the server, not user input — no escaping needed
  return `<span class="badge badge-${p}">${p}</span>`;
}

export function fmtMonths(m) {
  if (!m || m === Infinity) return '∞';
  const yrs = Math.floor(m / 12);
  const mos = m % 12;
  if (yrs === 0) return `${mos}mo`;
  return mos > 0 ? `${yrs}y ${mos}mo` : `${yrs}y`;
}

/**
 * Monday-Sunday boundaries of the week containing `date` (defaults to now),
 * as YYYY-MM-DD strings. Previously computed independently (and slightly
 * differently) in plan.js's Week View, pulse.js's renderPulse, and app.js's
 * openWeeklyReviewSheet — see KNOWN_INCONSISTENCIES #29.
 */
export function getWeekBounds(date = new Date()) {
  const dow = (date.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  const monday = new Date(date);
  monday.setHours(0, 0, 0, 0); // normalize before deriving date strings — a
  // late-night local time near midnight can otherwise cross into the next/
  // previous UTC calendar day once toISOString() converts it, silently
  // shifting weekStart/weekEnd by a day depending on timezone offset.
  monday.setDate(monday.getDate() - dow);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    monday,
    sunday,
    weekStart: monday.toISOString().split('T')[0],
    weekEnd:   sunday.toISOString().split('T')[0],
  };
}

/**
 * A brief, auto-dismissing confirmation toast, fixed above the tab bar.
 * Previously private to settings.js (used for every save/delete there) but
 * nowhere else — every other save handler in the app (tasks, income,
 * expenses, trades, blocks, commission, Roth, snapshots) gave no success
 * feedback at all beyond the sheet closing. Moved here so it's shared
 * (roadmap: "toast notifications for success actions").
 */
export function showToast(msg, isError = false) {
  const existing = document.getElementById('app-toast');
  if (existing) existing.remove();
  const toast = document.createElement('div');
  toast.id = 'app-toast';
  toast.textContent = msg;
  toast.style.cssText = `
    position:fixed;bottom:calc(var(--tab-bar-h) + 16px);left:50%;transform:translateX(-50%);
    background:${isError ? 'var(--negative)' : 'var(--bg-tertiary)'};color:var(--text-primary);
    padding:10px 20px;border-radius:99px;font-size:.85rem;font-weight:600;z-index:999;
    box-shadow:0 4px 20px rgba(0,0,0,.4);pointer-events:none;
    animation:fadeInUp .2s ease;
  `;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2500);
}

/**
 * Turns a sleep window (start time + duration) into the "awake" minute-of-day
 * segments the Schedule Assistant should search for open slots in. Deriving
 * this from the user's own settings (instead of a hardcoded waking window)
 * is what lets night-shift workers and night-class students — who are awake
 * during what a 7am-midnight assumption would treat as "off limits" — get
 * correct suggestions. See KNOWN_INCONSISTENCIES #26 Phase 2.
 *
 * A sleep window that doesn't cross midnight (e.g. 23:00 + 6h -> awake
 * 05:00-23:00) produces one same-day segment. One that does — a night-shift
 * sleeper resting 09:00-16:00 is awake 16:00 through 09:00 the *next* day —
 * produces two segments for any single calendar day: the tail end of last
 * night's awake stretch (00:00 to wake time) and the start of tonight's
 * (sleep start to 24:00). Segments are always bounded to a single day
 * because time_blocks (the underlying data model) can't span midnight.
 */
export function getWakingSegments(sleepStartTime, sleepDurationHours) {
  const [h, m] = (sleepStartTime ?? '00:00').split(':').map(Number);
  const sleepStartMin = h * 60 + m;
  const sleepDurationMin = Math.round((sleepDurationHours ?? 6) * 60);
  const awakeStart = (sleepStartMin + sleepDurationMin) % 1440; // wake time
  const awakeEnd   = sleepStartMin;                             // next bedtime

  if (awakeStart < awakeEnd) {
    return [{ start: awakeStart, end: awakeEnd }];
  }
  if (awakeStart > awakeEnd) {
    return [{ start: 0, end: awakeEnd }, { start: awakeStart, end: 1440 }].filter(s => s.end > s.start);
  }
  return [{ start: 0, end: 1440 }]; // 0-hour sleep duration — awake all day
}

/**
 * Minutes elapsed since the user's most recent wake-up, for a given
 * minute-of-day — computed circularly so it stays correct across midnight
 * (e.g. a night-shift worker who wakes at 16:00 and is still awake at 03:00
 * gets offset 660, not a negative number). Feeds getTimeOfDayThird below.
 */
function minutesSinceWake(minOfDay, wakeStartMin) {
  return ((minOfDay - wakeStartMin) % 1440 + 1440) % 1440;
}

/**
 * Classifies a minute-of-day as 'morning'/'afternoon'/'night' relative to
 * the user's *own* waking window — thirds of their total awake duration,
 * counted from their actual wake time — rather than literal clock hours.
 * This is what makes a task's time-of-day preference mean the same thing
 * for a conventional schedule and a night-shift one: "morning" is always
 * "shortly after I woke up," never hardcoded to 6-9am. See
 * KNOWN_INCONSISTENCIES #26 Phase 3.
 */
export function getTimeOfDayThird(minOfDay, sleepStartTime, sleepDurationHours) {
  const [h, m] = (sleepStartTime ?? '00:00').split(':').map(Number);
  const sleepStartMin = h * 60 + m;
  const sleepDurationMin = Math.round((sleepDurationHours ?? 6) * 60);
  const wakeStartMin = (sleepStartMin + sleepDurationMin) % 1440;
  const awakeDurationMin = 1440 - sleepDurationMin;

  const offset = minutesSinceWake(minOfDay, wakeStartMin);
  const third = awakeDurationMin / 3;
  if (offset < third) return 'morning';
  if (offset < 2 * third) return 'afternoon';
  return 'night';
}

/**
 * Whether a candidate slot (its start minute-of-day) satisfies a task's
 * scheduling preference. 'custom' compares against the task's own
 * preferredStartTime/EndTime range (wrapping past midnight if end < start);
 * morning/afternoon/night defer to getTimeOfDayThird; no preference always
 * matches.
 */
export function matchesTimePreference(slotStartMin, task, sleepStartTime, sleepDurationHours) {
  if (!task.preferredTimeOfDay) return true;
  if (task.preferredTimeOfDay === 'custom') {
    if (!task.preferredStartTime || !task.preferredEndTime) return true;
    const start = timeToMin(task.preferredStartTime);
    const end   = timeToMin(task.preferredEndTime);
    return start <= end
      ? slotStartMin >= start && slotStartMin < end
      : slotStartMin >= start || slotStartMin < end; // wraps midnight
  }
  return getTimeOfDayThird(slotStartMin, sleepStartTime, sleepDurationHours) === task.preferredTimeOfDay;
}

function timeToMin(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/* ── SHEET PRIMITIVE (UX_AUDIT W-2, and therefore W-1) ──────────────────────
 *
 * Every sheet in this app used to be opened by hand-repeating three lines:
 *
 *     overlay.classList.add('open');
 *     sheet.classList.add('open');
 *     overlay.addEventListener('click', closeSheet, { once: true });
 *
 * …at 18 call sites across app.js, progress.js and settings.js. That is W-2,
 * and W-1 is its consequence: adding Escape-to-close, a focus trap, or a
 * visible close control was an 18-edit change, so none of them existed. The
 * app had **zero** keydown handlers — every modal was a keyboard trap
 * (WCAG 2.1.2), and the only way out was clicking the overlay strip above the
 * sheet. The missing primitive was the finding; this is the primitive.
 *
 * `presentSheet()` owns dismissal so no caller has to remember it. Adding a
 * behaviour to every sheet in the app is now a one-line change here.
 */

let _sheetKeyHandler = null;
let _lastFocused     = null;
let _sheetObserver   = null;

/**
 * W-12: pin a sheet's trailing action row so the primary button stays on
 * screen in a form taller than the viewport. Detected, not declared — the last
 * element child qualifies only if it *is* a primary button or contains one, so
 * a sheet ending in explanatory text gets no bar.
 */
function _promoteActionRow(content) {
  const lastEl = content.lastElementChild;
  if (!lastEl) return;
  const isActionRow = lastEl.classList?.contains('btn-primary')
    || !!lastEl.querySelector?.('.btn-primary');
  lastEl.classList.toggle('sheet-actions', !!isActionRow);
}

/** Focusable descendants, in DOM order, excluding anything disabled/hidden. */
function _focusables(root) {
  return [...root.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),'
    + 'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
  )].filter((el) => el.offsetParent !== null || el === document.activeElement);
}

/**
 * Open the shared bottom sheet with dismissal, a focus trap and a close
 * control already wired. Call this instead of toggling `.open` by hand.
 *
 * @param {{label?: string, onClose?: () => void}} [opts]
 *        `label` names the dialog for screen readers; `onClose` runs after
 *        teardown (e.g. to re-render a list the sheet edited).
 */
export function presentSheet(opts = {}) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  if (!overlay || !sheet) return;

  _lastFocused = document.activeElement;
  _sheetOnClose = typeof opts.onClose === 'function' ? opts.onClose : null;

  // A dialog must announce itself, not just look like one.
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  if (opts.label) sheet.setAttribute('aria-label', opts.label);
  else sheet.removeAttribute('aria-label');

  // A visible, keyboard-reachable way out — previously the overlay strip was
  // the only one, and at 1440x900 that is 41% of the viewport and invisible
  // to a keyboard user entirely.
  let closeBtn = sheet.querySelector('.sheet-close');
  if (!closeBtn) {
    closeBtn = document.createElement('button');
    closeBtn.className = 'sheet-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => closeSheet());
    sheet.insertBefore(closeBtn, sheet.firstChild);
  }

  // W-12: a sheet taller than the viewport hid its own submit button below the
  // fold — the New Task sheet scrolls to 942px inside an 828px box, putting
  // "Add task" 94px past the bottom on open. Rather than edit the markup of
  // every sheet, promote the trailing action row to a sticky footer here, so
  // the primary action is always on screen. Detected rather than declared: the
  // last element child counts only if it actually contains a primary button.
  _promoteActionRow(content);

  // Several sheets are async and fill `#sheet-content` *after* opening (the
  // Weekly Review, Schedule Assistant, Inbox and Planning sheets all await an
  // API call behind a loading skeleton). A one-shot promotion above therefore
  // measured the skeleton and missed the real action row — caught live on the
  // Weekly Review, whose last child is the primary button itself and still did
  // not get the sticky treatment. Re-run on any content change while open.
  _sheetObserver = new MutationObserver(() => _promoteActionRow(content));
  _sheetObserver.observe(content, { childList: true, subtree: false });

  // The sheet element is shared and reused, so it keeps the scroll offset of
  // whatever was open last. Found while measuring W-12: reopening the task
  // sheet landed 108px down, mid-form. Reset before showing.
  sheet.scrollTop = 0;

  overlay.classList.add('open');
  sheet.classList.add('open');
  overlay.addEventListener('click', () => closeSheet(), { once: true });

  _sheetKeyHandler = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeSheet(); return; }
    if (e.key !== 'Tab') return;
    // Trap: Tab past either end wraps inside the sheet rather than escaping
    // to the page behind it, which is still visible and still clickable.
    const f = _focusables(sheet);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  document.addEventListener('keydown', _sheetKeyHandler);

  // Move focus in, so the first Tab lands inside the dialog rather than in
  // the page behind it. Prefer real content over the close button.
  const target = _focusables(content)[0] || closeBtn;
  requestAnimationFrame(() => target?.focus());
}

let _sheetOnClose = null;

export function closeSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  overlay?.classList.remove('open');
  sheet?.classList.remove('open');

  if (_sheetKeyHandler) {
    document.removeEventListener('keydown', _sheetKeyHandler);
    _sheetKeyHandler = null;
  }
  if (_sheetObserver) { _sheetObserver.disconnect(); _sheetObserver = null; }
  sheet?.removeAttribute('aria-modal');

  // Return focus where the user left it, or they are dumped at the top of the
  // document with no idea where they were.
  if (_lastFocused && document.contains(_lastFocused)) _lastFocused.focus();
  _lastFocused = null;

  const cb = _sheetOnClose;
  _sheetOnClose = null;
  if (cb) cb();
}

/**
 * The six system category colors, as literal hex — kept as real values (not
 * CSS var() references) because these feed a native <input type="color">
 * (settings.js's category swatch picker) and get stored as a category's
 * literal `color` string in the database; both require a real hex value, not
 * a CSS expression. Must match design-system.css's --cat-work/--cat-school/
 * --cat-growth/--cat-fun/--cat-rel/--cat-health tokens — previously
 * hardcoded independently in both settings.js and onboarding.js (two copies
 * that could silently drift apart); this is now the single source both
 * import from.
 */
export const CATEGORY_COLORS = {
  work:          '#3B7EF0',
  school:        '#A855F7',
  growth:        '#12B981',
  fun:           '#F97316',
  relationships: '#F43F8E',
  health:        '#06B6D4',
};
